import { min } from 'lodash';

import { Currency } from '../types/api/Currency/Currency';
import Enums from '../types/api/Enums';
import { ratioToPercent, formatDecimalString } from '../utils/decimals';
import { hasValue } from '../utils/equality';
import { logger } from '../utils/logger';

type PlantMarginOverridesReturn = {
  targetMarginOverride: string | Currency | null;
  minimumMarginOverride: string | Currency | null;
};

interface MarginOverrides {
  targetMarginPercentageOverride: string | null;
  minimumMarginPercentageOverride: string | null;
  targetMarginFlatAmountOverride: Currency | null;
  minimumMarginFlatAmountOverride: Currency | null;
}

// Selects which margin override values to use based on the configured margin type.
export const selectMarginOverride = (
  overrides: MarginOverrides | null,
  marginConfiguration: Enums.MarginConfiguration,
): PlantMarginOverridesReturn => {
  if (!hasValue(overrides)) {
    return {
      targetMarginOverride: null,
      minimumMarginOverride: null,
    };
  }

  if (marginConfiguration === Enums.MarginConfiguration.FlatDollar) {
    return {
      targetMarginOverride: overrides.targetMarginFlatAmountOverride,
      minimumMarginOverride: overrides.minimumMarginFlatAmountOverride,
    };
  }
  return {
    targetMarginOverride: overrides.targetMarginPercentageOverride,
    minimumMarginOverride: overrides.minimumMarginPercentageOverride,
  };
};

export const hasSelectedMarginOverride = (
  overrides: MarginOverrides | null,
  marginConfiguration: Enums.MarginConfiguration,
): boolean => {
  const { targetMarginOverride, minimumMarginOverride } = selectMarginOverride(
    overrides,
    marginConfiguration,
  );
  return targetMarginOverride !== null && minimumMarginOverride !== null;
};

type SelectMarginOverrideDisplayReturn = {
  targetMarginOverride: string;
  minimumMarginOverride: string;
};

// Selects which margin override values to use based on the configured margin type and makes them display-friendly.
export const selectMarginOverrideDisplay = (
  readyMixConstants: MarginOverrides | undefined | null,
  marginConfiguration: Enums.MarginConfiguration,
): SelectMarginOverrideDisplayReturn => {
  if (!hasValue(readyMixConstants)) {
    return {
      targetMarginOverride: '',
      minimumMarginOverride: '',
    };
  }

  if (marginConfiguration === Enums.MarginConfiguration.FlatDollar) {
    return {
      targetMarginOverride: readyMixConstants.targetMarginFlatAmountOverride?.number ?? '',
      minimumMarginOverride: readyMixConstants.minimumMarginFlatAmountOverride?.number ?? '',
    };
  }
  return {
    targetMarginOverride: ratioToPercent(readyMixConstants.targetMarginPercentageOverride, ''),
    minimumMarginOverride: ratioToPercent(readyMixConstants.minimumMarginPercentageOverride, ''),
  };
};

// toHumanReadableMargin converts the given percentage string or a currency to a human‑readable margin for display.
// null corresponds to '', invalid numeric value to 0%
export function toHumanReadableMargin(margin: string | Currency | null): string {
  if (margin === null) {
    return '';
  }

  // TODO(app.shortcut.com/slabstack/story/8026)
  if (margin instanceof Currency) {
    return `$${formatDecimalString(margin.number, 2)}`;
  }
  const number = parseFloat(margin);
  if (!Number.isFinite(number)) {
    return '0%';
  }
  const valueStr = formatDecimalString(number * 100, 2);
  return `${valueStr}%`;
}
